<template lang="pug">
div(id="locationmap" class="h-screen flex flex-col min-h-[640px] max-h-[850px] sm:h-auto sm:min-h-0")
  div(v-if="block.innerBlocks[0]" class="upper-cta-content")
    div(class="top-section top-section__mobile")
      Block(:block="block.innerBlocks[0].innerBlocks[0]")
      img(src="~/assets/img/desktop-header_seo-v2.webp" alt="Van" class="max-h-[120px]")
    Block(:block="block.innerBlocks[0].innerBlocks[1]")
  div.map-container.relative
    div(v-if="block.innerBlocks.length" class="floating-cta-container")
      .relative
        img(src="~/assets/img/desktop-header_seo-v2.webp" alt="Van" class="absolute -right-ds-lg -top-ds-lg rounded-tr-[32px] max-h-[140px] !min-h-0")
        Block(v-if="block.innerBlocks[0]" :block="block.innerBlocks[0]" class="top-section mb-ds-ml")
        Block(v-if="block.innerBlocks[1]" :block="block.innerBlocks[1]")

    .absolute.w-full.h-full.bg-gray-100.z-20(v-show="!mapReady")
      .absolute.inset-0.grid.grid-cols-4.grid-rows-4
        template(v-for="n in 16" :key="n")
          .border.border-gray-200
      .absolute.inset-0.flex.items-center.justify-center(class="bg-white/50")
        .flex.flex-col.items-center.gap-ds-ml
          svg.text-blue-400.animate-bounce(
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            width="40px"
            height="40px"
          )
            path(
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 21s-8-4.5-8-11a8 8 0 1 1 16 0c0 6.5-8 11-8 11z"
            )
            circle(cx="12" cy="10" r="3")
          span.text-gray-600.text-ds-md {{ $t('go_local.loading_moves') }}
    div(id="map" ref="mapContainer" class="absolute top-0 left-0 w-full h-full z-10")

    <!-- top and bottom shadow -->
    div(class="absolute top-0 left-0 w-full h-ds-sm shadow-inner-top pointer-events-none z-20")
    div(class="absolute bottom-0 left-0 w-full h-ds-sm shadow-inner-bottom pointer-events-none z-20")
  div(v-if="block.innerBlocks[1]" class="lower-cta-content")
    Block(:block="block.innerBlocks[1]")
</template>

<script lang="ts">
import { defineComponent } from "vue"
import mapboxgl, { NavigationControl } from "mapbox-gl"
import { mapGetters } from "vuex"
import { getScreenWidth } from "~/scripts/useful-functions"
import { GET_MAP_LOCATION } from "~/store/location"

interface PinData {
  id: number
  lat: number
  lng: number
  imageID: number
  textLine1: string
  textLine2: string
  textLine3: string
  stars: number
  startOpen?: boolean
}

const MOVE_TO_PIN_DURATION = 1000
const PIN_POPUP_DELAY = 1500
const INITIAL_ZOOM = 13
const SCREEN_LG = 992

export default defineComponent({
  name: "MapBoxLocalMoves",
  props: {
    block: {
      required: true,
      type: Object as () => Block
    }
  },
  data() {
    return {
      accessToken: this.$config.public.mapBoxKey,
      map: null as mapboxgl.Map | null,
      markerIconUrl: "/content-static/icons/local-moves-pin-orange.svg",
      mapControl: null as NavigationControl | null,
      mapReady: false
    }
  },
  computed: {
    ...mapGetters("location", {
      pins: GET_MAP_LOCATION
    })
  },
  mounted() {
    if (this.pins.length > 0) {
      this.initializeMap()
    }
  },
  methods: {
    initializeMap(): void {
      const isMobile = (getScreenWidth() || 0) < SCREEN_LG
      const bounds = new mapboxgl.LngLatBounds()
      this.pins.forEach(pin => {
        bounds.extend([pin.lng, pin.lat])
      })

      mapboxgl.accessToken = this.$config.public.mapBoxKey
      this.map = new mapboxgl.Map({
        container: this.$refs.mapContainer as HTMLElement,
        style: "mapbox://styles/mapbox/streets-v11",
        attributionControl: false,
        pitchWithRotate: false,
        touchPitch: false,
        dragRotate: false,
        bounds,
        maxZoom: INITIAL_ZOOM + 2,
        minZoom: INITIAL_ZOOM - 4,
        fitBoundsOptions: {
          padding: { top: 0, bottom: 0, left: this.getLeftPadding(), right: 0 },
          maxZoom: INITIAL_ZOOM
        },
        cooperativeGestures: isMobile
      })

      this.map.on("load", () => {
        if (!this.map) return
        this.addPinsToMap()
        this.setMapBoundaries()

        // hide labels
        this.map.setLayoutProperty("country-label", "visibility", "none")
        this.map.setLayoutProperty("state-label", "visibility", "none")
        this.map.setLayoutProperty("settlement-label", "visibility", "none")
        this.map.setLayoutProperty("road-label", "visibility", "none")
        this.map.setLayoutProperty("poi-label", "visibility", "none")
        this.map.setLayoutProperty("road-label-motorway", "visibility", "none")
        this.map.setLayoutProperty("road-label-major", "visibility", "none")
        this.map.setLayoutProperty("road-number-shield", "visibility", "none")
        this.map.setLayoutProperty("transit-station-label", "visibility", "none")

        requestAnimationFrame(() => {
          this.mapReady = true
        })
      })

      this.map.scrollZoom.disable()
      this.mapControl = new mapboxgl.NavigationControl({ showCompass: false })
      this.map.addControl(this.mapControl, "bottom-right")
    },
    addPinsToMap(): void {
      if (!this.map) return

      this.pins.forEach(pin => {
        // create a custom marker element
        const markerElement = document.createElement("div")
        markerElement.className = "av-marker"

        // create an image element to hold the SVG
        const img = document.createElement("img")
        img.src = this.markerIconUrl
        img.alt = "Map marker"
        markerElement.appendChild(img)

        markerElement.addEventListener("click", () => {
          this.centerMapOnPin(pin)
        })

        // create the star rating div
        const starsDiv = this.createStarRating(pin.stars)

        const popup = new mapboxgl.Popup({
          offset: 25,
          className: "av-popup",
          anchor: "bottom"
        }).setHTML(
          `<div class="av-popup-container"><img src="/content-static/map/${pin.imageID}.webp" loading="lazy" /><div class="flex flex-col gap-ds-xxxs text-sm"><div>${pin.textLine1}</div><div>${pin.textLine2}</div><div>${pin.textLine3}</div>${starsDiv.outerHTML}</div></div>`
        )

        // create a marker with the custom element
        new mapboxgl.Marker(markerElement)
          .setLngLat([pin.lng, pin.lat])
          .setPopup(popup)
          .addTo(this.map as mapboxgl.Map)

        if (pin.startOpen) {
          // delay by 3 seconds
          setTimeout(() => {
            popup.addTo(this.map!)
          }, PIN_POPUP_DELAY)
        }
      })
    },
    centerMapOnPin(pin: PinData): void {
      if (!this.map) return

      const bounds = new mapboxgl.LngLatBounds()
      bounds.extend([pin.lng, pin.lat])

      this.centerMapOnBounds(bounds)
    },
    centerMapOnBounds(
      bounds: mapboxgl.LngLatBounds,
      animationDuration: number = MOVE_TO_PIN_DURATION
    ): void {
      if (!this.map) return

      this.map.fitBounds(bounds, {
        maxZoom: 13,
        duration: animationDuration,
        padding: {
          top: this.getTopPadding(),
          bottom: 0,
          left: this.getLeftPadding(),
          right: 0
        }
      })
    },
    calculatePinsBounds(): mapboxgl.LngLatBounds {
      const bounds = new mapboxgl.LngLatBounds()
      this.pins.forEach(pin => {
        bounds.extend([pin.lng, pin.lat])
      })
      return bounds
    },
    setMapBoundaries(): void {
      if (!this.map) return
      const currentBounds = this.map.getBounds()
      if (!currentBounds) return

      // calculate the width and height of the current view
      const width = currentBounds.getEast() - currentBounds.getWest()
      const height = currentBounds.getNorth() - currentBounds.getSouth()

      // calculate new bounds that extend the current view in all directions
      const maxBounds = new mapboxgl.LngLatBounds(
        [currentBounds.getWest() - width, currentBounds.getSouth() - height],
        [currentBounds.getEast() + width, currentBounds.getNorth() + height]
      )

      // set the maximum bounds
      this.map.setMaxBounds(maxBounds)
    },
    createStarRating(rating) {
      const starsDiv = document.createElement("div")
      starsDiv.className = "flex gap-ds-xxxs"
      const fullStars = Math.floor(rating)
      const hasHalfStar = rating % 1 !== 0

      // add full stars
      starsDiv.innerHTML =
        "<img class='star' src='/content-static/icons/star-soft-yellow.svg'/>".repeat(fullStars)

      if (hasHalfStar) {
        starsDiv.innerHTML +=
          "<img class='star' src='/content-static/icons/half-star-soft-yellow.svg'/>"
      }

      return starsDiv
    },
    getLeftPadding() {
      const screenWidth = getScreenWidth()
      if (screenWidth && screenWidth >= SCREEN_LG) {
        const map = this.$refs.mapContainer as HTMLElement
        return map.clientWidth / 2
      }
      return 0
    },
    getTopPadding() {
      const screenWidth = getScreenWidth()
      if (screenWidth && screenWidth < SCREEN_LG) {
        const map = this.$refs.mapContainer as HTMLElement
        return map.clientHeight / 2
      }
      return 0
    },
    cleanupMap() {
      if (this.map) {
        if (this.mapControl) {
          this.map.removeControl(this.mapControl)
        }
        this.map.remove()
        this.map = null
      }
    }
  },
  beforeUnmount() {
    this.cleanupMap()
  }
})
</script>

<style lang="css">
@import "mapbox-gl/dist/mapbox-gl.css";

.mapboxgl-ctrl-group {
  @apply rounded-xl;

  button {
    @apply w-[40px] h-[40px];
  }
}

.top-section {
  &.top-section__mobile {
    @apply flex justify-between items-center gap-ds-sm;
  }
  h1 {
    span.av-paragraph {
      @apply text-rebrandSm leading-normal mb-0 font-normal;
    }
    span.av-heading {
      @apply mb-ds-md lg:mb-ds-ml;
    }

    @screen lg {
      .av-paragraph {
        @apply text-black50 text-rebrandMd;
      }
    }
  }
}

.mapbox-local-moves-map > .content {
  @apply p-0 max-w-none;
}

.upper-cta-content {
  @apply relative flex lg:hidden flex-col justify-between w-full pb-ds-md pl-ds-md pr-0 bg-blue-primary text-white;

  .av-heading {
    @apply mb-0;
  }

  .av-paragraph {
    @apply mb-0 pr-ds-md leading-normal;
  }

  h3 {
    @apply mb-ds-sm;
  }
  .highlight {
    @apply text-anyvan-yellow lg:text-blue-primary;
  }
}

.lower-cta-content {
  @apply flex lg:hidden w-full p-ds-md;

  .highlight {
    @apply text-anyvan-yellow lg:text-blue-primary;
  }
}

.home-removals-title {
  @apply font-bold lg:font-normal;
}

.fact-highlight {
  @apply text-anyvan-yellow lg:text-blue-primary;
}

.map-container {
  @apply relative w-full h-[220px] sm:h-[440px] lg:h-[596px] flex-grow;
}

.floating-cta-container {
  @apply hidden lg:flex absolute z-30 bg-white p-ds-lg w-[612px] rounded-4xl h-auto flex-col justify-between overflow-hidden lg:ml-[80px] xl:ml-ds-xxxl;
  box-shadow: theme("boxShadow.rebrand");
  top: 50%;
  transform: translateY(-50%);

  @screen rebrandLg {
    margin-left: calc(((100vw - 1512px) / 2) + 96px);
  }

  .av-heading {
    @apply mb-ds-ml max-w-[85%];
  }

  .av-paragraph {
    @apply leading-normal;
  }

  .highlight {
    @apply text-anyvan-yellow lg:text-blue-primary;
  }
}

.shadow-inner-top {
  box-shadow: inset 0 8px 8px -8px rgba(0, 0, 0, 0.3);
}

.shadow-inner-bottom {
  box-shadow: inset 0 -8px 8px -8px rgba(0, 0, 0, 0.3);
}

.av-popup {
  min-width: 290px;
  height: auto;

  .mapboxgl-popup-content {
    @apply bg-anyvan-yellow p-0 pr-ds-lg rounded-2xl w-fit h-full;
    box-shadow: theme("boxShadow.rebrand");

    .av-popup-container {
      @apply flex items-center;

      > img {
        @apply pl-ds-sm h-[80px] w-[80px];
      }

      > div {
        padding: 10px 0 10px 8px;
      }

      .star {
        @apply h-ds-md w-ds-md;
      }
    }

    .mapboxgl-popup-close-button {
      @apply flex items-center justify-center w-ds-ml h-ds-ml rounded-full bg-white text-black absolute top-0 right-0 m-ds-xxs opacity-50 cursor-pointer pb-ds-xxs;

      &:hover {
        @apply opacity-80;
      }
    }
  }

  .mapboxgl-popup-tip {
    border-top-color: theme("colors.anyvan.yellow");
    border-bottom-color: theme("colors.anyvan.yellow");
  }
}

.av-marker {
  @apply cursor-pointer w-ds-lg;

  img {
    @apply w-full h-full;
    transition: transform 0.3s;
  }

  &:hover img {
    transform: scale(1.2);
  }
}

.floating-cta-container {
  @apply bg-white w-[570px];
}

.has-primary-color {
  @apply text-blue-primary;
}
</style>
